/**
 * Upload real do anexo do composer (spec-016 T023 — fecha o gap G3/G-4).
 *
 * Extraído do host pelo mesmo motivo dos outros dois mixins da pasta
 * (`send-message-altura-mixin.js`, `send-message-catalogo-comandos-mixin.js`):
 * `send-message.vue` já passava do teto de linhas do repo. Lê/escreve
 * via `this` compartilhado — precisa de `this.arquivos` (a fila do composer,
 * dona do host) e `this.idClienteAnexo` (prop nova).
 *
 * ★ [spec-010-r2-guiada-pelo-agente-com-transcricao, T006] DOIS canais de
 * ORIGEM hoje, escolhidos por EXTENSÃO (`utils/canal-do-anexo.js::canalDoArquivo`):
 *
 * | canal | rota | extensões |
 * |---|---|---|
 * | carteira | `POST /fee-fixo/clientes/carteira-chat/upload` | `.pdf .png .jpg .jpeg .webp .xlsx .xls .csv` |
 * | transcrição | `POST /v3/api/aawz-transcriptions/transcriptions/upload-texto` | `.txt .md .vtt .docx` |
 *
 * ★ [17/09/2026, spec-009-anexo-sem-trava, T008] Até aqui os DOIS exigiam
 * `id_cliente_app` obrigatório, e quem anexava sem cliente em foco caía em
 * `status: 'sem_cliente'`, parado até alguém resolver o cliente. Hoje isso só
 * vale para o canal de TRANSCRIÇÃO — sem equivalente de análise, fora do
 * escopo desta spec (research §4). O canal de CARTEIRA ganhou uma saída: sem
 * cliente, o arquivo sobe pelo canal de ANÁLISE (`apiAnexosAnalise`,
 * `POST /anexos-analise`, `id_cliente_app` OPCIONAL — mesmo conjunto de
 * extensões do canal de carteira, `contracts/anexo-analise.md` §1) — a IA lê
 * o arquivo sem exigir cadastro; vincular a um cliente é passo POSTERIOR
 * (W5, fora desta wave). `status: 'sem_cliente'` sobrevive só para
 * transcrição sem cliente. Extensão fora das duas listas (`canalDoArquivo`
 * devolve `null`) continua recusada AQUI, sem nenhuma chamada de rede —
 * `marcarErroDeUpload` com a mensagem que nomeia o arquivo e as duas listas
 * aceitas (`mensagemCanalDesconhecido`).
 *
 * O QUE ESTE MIXIN GARANTE:
 * - Canal de carteira, sem cliente → sobe pela análise IMEDIATAMENTE (nunca
 *   espera resolução de cliente).
 * - Canal de transcrição, sem cliente → `idClienteAnexo` chega DEPOIS
 *   (usuário seleciona cliente após anexar) → `tentarUploadPendentes()` sobe
 *   os itens que ficaram `sem_cliente` — o canal é resolvido de novo pela
 *   mesma extensão, sem guardar estado extra.
 * - Nunca dois uploads concorrentes do MESMO arquivo (`chaveDoArquivo`
 *   idempotente — reanexar o mesmo file antes do primeiro terminar é no-op).
 * - `anexosProntos` só lista os que TERMINARAM com sucesso, na ORDEM da fila
 *   — é isto que `onSend` (host) manda como 3º argumento do evento `send`.
 *   O `uploadId` genérico é `upload_id` no canal de carteira/análise e
 *   `transcription_id` no de transcrição — o consumidor (`apiChat._buildChatBody`)
 *   só o repassa como referência opaca ao modelo, então os três cabem na
 *   MESMA forma sem distinção de canal.
 * - ★ [T009] O envio NUNCA espera todo anexo ficar pronto: `removerAnexosEnviados()`
 *   (chamado por `onSend()` do host) some da fila SÓ quem terminou (`enviado`)
 *   — o que ficou (`enviando`/`erro`/`sem_cliente`) continua esperando o
 *   próximo envio, e o motivo de cada um vai para o texto do turno
 *   (`itensNaoIncluidosNoEnvio`, computed abaixo).
 */
import { apiFeeFixoCarteiraChat } from '@modules/fee-fixo/api/apiFeeFixoCarteiraChat'
import { apiTranscricaoUploadTexto } from '@modules/aawz-intelligence-v2/api/apiTranscricaoUploadTexto'
import { apiAnexosAnalise } from '@modules/aawz-intelligence-v2/api/apiAnexosAnalise'
import { chaveDoArquivo } from '@modules/aawz-intelligence-v2/utils/chave-de-arquivo'
import {
  CANAL_CARTEIRA,
  CANAL_TRANSCRICAO,
  canalDoArquivo,
  mensagemCanalDesconhecido,
} from '@modules/aawz-intelligence-v2/utils/canal-do-anexo'
import { COMPOSER_CHAT } from '@modules/aawz-intelligence-v2/constants/TextChat'
// Com alias: os computeds abaixo têm o mesmo nome das leituras puras, e um
// `grep` sem o prefixo misturava util e computed.
import {
  descartarFilaDeAnexos,
  filaDeAnexos as lerFilaDeAnexos,
  obterOuCriarFila,
  registrarAnexoAguardandoCliente,
} from '@modules/aawz-intelligence-v2/utils/anexos-pendentes'
import {
  anexosProntos as lerAnexosProntos,
  statusUploadParaExibir as lerStatusUploadParaExibir,
} from '@modules/aawz-intelligence-v2/utils/anexos-da-fila'
import { STATUS_UPLOAD } from './status-de-upload'

// Prefixo da chave de fila de uma instância SEM `chaveDaFilaDeAnexos`.
const PREFIXO_FILA_DA_INSTANCIA = 'composer-'

export const sendMessageAnexosUploadMixin = {
  props: {
    /**
     * A fila de anexos que ESTA instância compartilha com outras
     * (`utils/anexos-pendentes.js`). Vazio = fila própria da instância, que
     * morre com ela — o default do popup, do Hub, do Fee-Based e do
     * ai-framework. Só `view-chat.vue` passa uma chave: herói e rodapé se
     * alternam por `v-if` e o anexo pendente precisa sobreviver à troca.
     */
    chaveDaFilaDeAnexos: {
      type: String,
      default: '',
    },
    /**
     * O cliente que vai RECEBER a carteira anexada — `app_clientes.id`.
     *
     * Espelha o `idClienteEmFoco` que a view já calcula (escopo travado ou
     * `clientePreFiltro`); o composer não lê store, então precisa receber
     * pronto. `null`/`undefined` é o estado "sem cliente resolvido ainda" —
     * NÃO dispara upload algum (ver limitação no cabeçalho do arquivo).
     */
    idClienteAnexo: {
      type: Number,
      default: null,
    },
    /**
     * O NOME do cliente de `idClienteAnexo`, só para a tela dizer o destino
     * antes do envio (`send-message.vue::destinoDoAnexo`, teste-11 09/09/2026).
     * Nunca entra no payload do upload — quem manda é o id.
     */
    nomeClienteAnexo: {
      type: String,
      default: '',
    },
  },
  computed: {
    // A chave da fila desta instância — a do pai ou uma só dela.
    chaveDaFila() {
      return this.chaveDaFilaDeAnexos || PREFIXO_FILA_DA_INSTANCIA + this._uid
    },
    /**
     * DÍVIDA-anexo (05/09): a fila e os três mapas por chave vivem FORA da
     * instância (`utils/anexos-pendentes.js`), para sobreviver à troca
     * herói↔rodapé de `view-chat.vue`. Antes eram `data()` desta instância,
     * e um upload `'sem_cliente'` marcado no herói desaparecia quando o
     * rodapé (instância NOVA) montava. Os proxies abaixo só EXPÕEM a fila —
     * quem a CRIA é o `created()`, para o getter ficar sem efeito colateral.
     */
    filaDeAnexos() {
      return lerFilaDeAnexos(this.chaveDaFila)
    },
    // chave → 'enviando' | 'enviado' | 'erro' | 'sem_cliente'
    statusUploadPorChave: {
      get() {
        return this.filaDeAnexos.statusUploadPorChave
      },
      set(valor) {
        this.filaDeAnexos.statusUploadPorChave = valor
      },
    },
    // chave → { uploadId, nome, contentType, tamanho } (só os 'enviado')
    uploadPorChave: {
      get() {
        return this.filaDeAnexos.uploadPorChave
      },
      set(valor) {
        this.filaDeAnexos.uploadPorChave = valor
      },
    },
    // chave → mensagem REAL do backend (400/403) ou do texto genérico
    // quando a falha não trouxe `message` (rede, exception) — [Decisão G-4]
    // "erro do upload nunca engolido". Só existe para chaves com
    // status 'erro'.
    mensagemErroPorChave: {
      get() {
        return this.filaDeAnexos.mensagemErroPorChave
      },
      set(valor) {
        this.filaDeAnexos.mensagemErroPorChave = valor
      },
    },
    // Os dois abaixo são as leituras puras de `utils/anexos-da-fila.js` —
    // o porquê de cada regra está lá.
    anexosProntos() {
      return lerAnexosProntos(this.arquivos, this.uploadPorChave)
    },
    statusUploadParaExibir() {
      return lerStatusUploadParaExibir(this.statusUploadPorChave)
    },
    /**
     * ★ [T009, spec-009] Os `File` crus cujo upload TERMINOU com sucesso —
     * mesmo CONJUNTO de `anexosProntos` (que carrega o metadado do upload),
     * mas o arquivo em si: é o que `onSend()` do host manda como BOLHA do
     * usuário (2º argumento do `@send`) e o que sai da fila depois do envio
     * (`removerAnexosEnviados`). Antes do T009 a bolha mandava a fila
     * INTEIRA, porque o invariante "só envia com tudo pronto" garantia que
     * os dois conjuntos eram sempre iguais — hoje não são mais.
     */
    arquivosEnviaveis() {
      return this.arquivos.filter(
        (arquivo) => this.statusUploadPorChave[chaveDoArquivo(arquivo)] === STATUS_UPLOAD.ENVIADO
      )
    },
    /**
     * ★ [T009, spec-009] O que NÃO vai no envio que está saindo agora —
     * `enviando`, `erro` ou `sem_cliente`. Cada item nomeia o MOTIVO (a
     * mensagem real do servidor quando existe) para `textoDoEnvio` (host)
     * dizer, no turno, por que aquele arquivo ficou de fora — nada
     * desaparece em silêncio, e a fila os preserva
     * (`removerAnexosEnviados` só tira quem terminou).
     */
    itensNaoIncluidosNoEnvio() {
      return this.arquivos
        .filter(
          (arquivo) => this.statusUploadPorChave[chaveDoArquivo(arquivo)] !== STATUS_UPLOAD.ENVIADO
        )
        .map((arquivo) => {
          const chave = chaveDoArquivo(arquivo)
          return { nome: arquivo.name, motivo: this._motivoDeNaoIncluido(chave) }
        })
    },
    /**
     * Existe anexo que precisa de AÇÃO — `'sem_cliente'` ou `'erro'`.
     *
     * ★ Não inclui `'enviando'` de propósito: aquele é transitório (o upload
     * termina em segundos), e piscar o aviso durante ele seria ruído — o
     * spinner na miniatura já avisa que algo está em curso.
     *
     * ★ [17/09/2026] É também o predicado do BLOQUEIO do envio
     * (`send-message.vue::canSend`/`rotuloBotaoAcao`): o aviso na tela e o
     * motivo do botão travado são a MESMA condição, nunca duas parecidas.
     */
    existeAnexoNaoEnviado() {
      return this.arquivos.some((arquivo) => {
        const status = this.statusUploadPorChave[chaveDoArquivo(arquivo)]
        return status === STATUS_UPLOAD.SEM_CLIENTE || status === STATUS_UPLOAD.ERRO
      })
    },
    /** O motivo mais comum de não-enviado: falta cliente em foco. */
    existeAnexoSemCliente() {
      return this.arquivos.some(
        (arquivo) => this.statusUploadPorChave[chaveDoArquivo(arquivo)] === STATUS_UPLOAD.SEM_CLIENTE
      )
    },
  },
  watch: {
    // Cliente resolvido DEPOIS do anexo (usuário seleciona na tela, ou a
    // conversa muda de escopo) — sobe os que ficaram esperando.
    idClienteAnexo(novo, velho) {
      if (novo && !velho) this.tentarUploadPendentes()
    },
    /**
     * A fila nasce (ou é reencontrada) a cada CHAVE — não no `created`.
     *
     * ⚠️ [20/09/2026, review do #2342] Com as conversas vivas a chave passou a
     * MUDAR sem o composer remontar (`view-chat.vue::chaveDaFilaDeAnexos` leva
     * a viva). Criando só no `created`, a aba nova ficava sem fila registrada:
     * `filaDeAnexos` caía no ramo do objeto ÓRFÃO — fora do registro e não
     * reativo —, a miniatura nunca aparecia e a mensagem saía SEM o anexo, em
     * silêncio. `immediate` cobre a montagem, que era o que o `created` fazia.
     */
    chaveDaFila: {
      immediate: true,
      handler(chave) {
        obterOuCriarFila(chave)
      },
    },
  },

  beforeDestroy() {
    // Fila própria da instância não tem quem a leia depois daqui.
    if (!this.chaveDaFilaDeAnexos) descartarFilaDeAnexos(this.chaveDaFila)
  },
  methods: {
    /**
     * Ponto de entrada — chamado pelo host a cada arquivo NOVO aceito em
     * `onAdicionarArquivos`. Idempotente por chave: reanexar o mesmo `File`
     * enquanto o upload dele está em andamento ou já terminou é no-op.
     *
     * ★ [T004b] Devolve a Promise do upload (antes era fire-and-forget) —
     * `tentarUploadPendentes()` precisa saber QUANDO o lote inteiro termina,
     * para só então decidir o turno de continuação. Chamadores que não
     * precisam esperar (o `forEach` de `onAdicionarArquivos`) continuam
     * ignorando o retorno normalmente.
     */
    iniciarUploadDoArquivo(arquivo) {
      const chave = chaveDoArquivo(arquivo)
      if (this._uploadJaEmAndamentoOuConcluido(chave)) return undefined

      // Extensão sem canal (nem carteira, nem transcrição): recusa ANTES de
      // qualquer coisa — inclusive antes do guard de cliente, porque um
      // `.exe` não tem para onde subir mesmo com cliente resolvido.
      const canal = canalDoArquivo(arquivo)
      if (!canal) {
        this.marcarErroDeUpload(chave, mensagemCanalDesconhecido(arquivo))
        return undefined
      }

      // ★ [T008, spec-009] Sem cliente, um arquivo do canal de CARTEIRA sobe
      // pelo canal de ANÁLISE (id_cliente_app opcional) — nunca mais fica
      // `sem_cliente` esperando alguém resolver o cadastro. O canal de
      // TRANSCRIÇÃO não tem equivalente de análise (fora do escopo desta
      // spec, research §4) e continua esperando o cliente.
      const semCliente = !this.idClienteAnexo
      if (semCliente && canal !== CANAL_CARTEIRA) {
        this.$set(this.statusUploadPorChave, chave, STATUS_UPLOAD.SEM_CLIENTE)
        return undefined
      }

      return this._dispararUpload(canal, arquivo, chave, semCliente)
    },
    /**
     * ★ [spec-009 T013, US-3] O anexo subiu SEM cliente: ele entra na lista de
     * espera do vínculo, de onde o chip de cliente o tira depois
     * (`view-chat.vue::vincularAnexosAoCliente` → `POST /anexos-analise/
     * {upload_id}/cliente`). É o que permite dar dono ao arquivo mais tarde
     * **sem reanexá-lo** (US-3).
     *
     * Registra a partir da FILA (não de `this.uploadPorChave`): com a fila
     * zerada por uma troca de conversa durante o upload, o computed já aponta
     * para a fila NOVA, e o guard de geração aqui é o mesmo do desfecho —
     * um anexo do cliente anterior não pode entrar na espera da conversa nova.
     *
     * Sem `uploadId` não há nada a vincular (upload que falhou) — e o
     * `registrar` é idempotente por `uploadId`, então reanexar o mesmo
     * arquivo não duplica a chamada de vínculo.
     */
    _registrarEsperaDeCliente(fila, geracaoAoSubir, chave) {
      if (fila.geracao !== geracaoAoSubir) return
      const upload = fila.uploadPorChave[chave]
      if (!upload || !upload.uploadId) return
      registrarAnexoAguardandoCliente(this.chaveDaFila, {
        uploadId: upload.uploadId,
        nome: upload.nome,
      })
    },
    /** `true` quando reanexar o MESMO `File` é no-op (upload em voo ou já concluído). */
    _uploadJaEmAndamentoOuConcluido(chave) {
      const status = this.statusUploadPorChave[chave]
      return status === STATUS_UPLOAD.ENVIANDO || status === STATUS_UPLOAD.ENVIADO
    },
    /**
     * Dispara a chamada de rede do canal já resolvido — isolado de
     * `iniciarUploadDoArquivo` só para caber no teto de `max-statements` do
     * ESLint (a lógica é a mesma de sempre, sem mudança de comportamento).
     */
    _dispararUpload(canal, arquivo, chave, semCliente) {
      this.$set(this.statusUploadPorChave, chave, STATUS_UPLOAD.ENVIANDO)
      // Fila zerada com o upload em voo (troca de conversa): o desfecho
      // gravaria a carteira do cliente ANTERIOR na fila nova, e reanexar o
      // arquivo a mandaria no turno de OUTRO cliente. A geração é lida do
      // OBJETO no desfecho — computed de instância destruída fica congelado.
      const fila = this.filaDeAnexos
      const geracaoAoSubir = fila.geracao
      return this._uploadNoCanal(canal, arquivo, semCliente)
        .then((r) => {
          this._aplicarDesfechoDoUpload(fila, geracaoAoSubir, chave, arquivo, r)
          if (semCliente) this._registrarEsperaDeCliente(fila, geracaoAoSubir, chave)
        })
        // Erro de REDE/exception (Promise rejeitada) — nunca deixar sem
        // `.catch`, senão vira unhandled rejection e o usuário não vê nada.
        // ★ Não relança: o lote (`tentarUploadPendentes`) precisa saber que
        // TERMINOU (com sucesso ou erro), nunca que a Promise.all rejeitou.
        .catch(() => {
          if (fila.geracao !== geracaoAoSubir) return
          this.marcarErroDeUpload(chave, null)
        })
    },
    /**
     * O desfecho de UM upload (sucesso ou erro do servidor) — isolado de
     * `_dispararUpload` só para caber no teto de `max-statements` do
     * ESLint (a lógica é a mesma de sempre, sem mudança de comportamento).
     */
    _aplicarDesfechoDoUpload(fila, geracaoAoSubir, chave, arquivo, r) {
      if (fila.geracao !== geracaoAoSubir) return
      // `upload_id` (canal carteira/análise) ou `transcription_id` (canal
      // transcrição) — os três viajam como a MESMA referência opaca
      // (`uploadId`) para o corpo do turno (ver cabeçalho do arquivo).
      const uploadId = r && r.value && (r.value.upload_id || r.value.transcription_id)
      if (r && r.status === 200 && uploadId) {
        this.$set(this.uploadPorChave, chave, {
          uploadId: uploadId,
          nome: arquivo.name,
          contentType: arquivo.type,
          tamanho: arquivo.size,
        })
        this.$set(this.statusUploadPorChave, chave, STATUS_UPLOAD.ENVIADO)
        return
      }
      this.marcarErroDeUpload(chave, r && r.message)
    },
    /** A chamada de rede do canal certo — isolado para os três caminhos não
     * duplicarem o parse do retorno em `iniciarUploadDoArquivo`.
     *
     * ★ [T008] `semCliente` só é `true` para o canal de CARTEIRA (a única
     * combinação que `iniciarUploadDoArquivo` permite chegar aqui) — quem
     * decide o canal de análise é o CHAMADOR, este método só executa. */
    _uploadNoCanal(canal, arquivo, semCliente) {
      if (semCliente) {
        return apiAnexosAnalise.upload({ arquivo: arquivo })
      }
      if (canal === CANAL_TRANSCRICAO) {
        return apiTranscricaoUploadTexto.upload({
          idClienteApp: this.idClienteAnexo,
          arquivo: arquivo,
        })
      }
      return apiFeeFixoCarteiraChat.upload({
        idClienteApp: this.idClienteAnexo,
        files: [arquivo],
      })
    },
    /**
     * ★ [T009] O motivo pelo qual um item ficou de fora do envio — texto
     * pronto para o turno (`itensNaoIncluidosNoEnvio`). `erro` usa a
     * mensagem REAL do backend (`mensagemErroPorChave`), nunca genérica
     * quando ela existe.
     */
    _motivoDeNaoIncluido(chave) {
      const status = this.statusUploadPorChave[chave]
      if (status === STATUS_UPLOAD.ERRO) {
        return this.mensagemErroPorChave[chave] || COMPOSER_CHAT.erroUploadAnexo
      }
      if (status === STATUS_UPLOAD.SEM_CLIENTE) return COMPOSER_CHAT.anexoSemCliente
      if (status === STATUS_UPLOAD.ENVIANDO) return COMPOSER_CHAT.anexoAindaSubindo
      return COMPOSER_CHAT.erroUploadAnexo
    },
    /** Marca `status: 'erro'` e guarda a mensagem REAL (ou o texto genérico). */
    marcarErroDeUpload(chave, mensagemDoBackend) {
      this.$set(this.statusUploadPorChave, chave, STATUS_UPLOAD.ERRO)
      this.$set(
        this.mensagemErroPorChave,
        chave,
        mensagemDoBackend || COMPOSER_CHAT.erroUploadAnexo
      )
    },
    /**
     * Re-tenta os itens que ficaram `sem_cliente` (watch acima).
     *
     * ★ Roda direto sobre `this.arquivos`: uma fonte de verdade só
     * (`this.arquivos` + `statusUploadPorChave`). A versão anterior mantinha
     * uma cópia própria (`arquivosPendentesPorChave`) porque o host zerava a
     * fila inteira a cada envio.
     *
     * ★ [17/09/2026] RETOMAR O UPLOAD é tudo o que este método faz. Até aqui
     * ele também disparava um turno de CONTINUAÇÃO automático ("Anexos
     * enviados: …") assim que o lote terminava de subir — medido ao vivo: a
     * pessoa escolhia o cliente no chip e a mensagem PARTIA sozinha, sem
     * Enter nem clique. Envio é gesto de quem escreve, nunca efeito colateral
     * de escolher um cliente; com o anexo pronto, o botão simplesmente
     * destrava (`send-message.vue::canSend`) e o próximo envio o leva junto.
     */
    tentarUploadPendentes() {
      this.arquivos
        .filter(
          (arquivo) =>
            this.statusUploadPorChave[chaveDoArquivo(arquivo)] === STATUS_UPLOAD.SEM_CLIENTE
        )
        .forEach((arquivo) => this.iniciarUploadDoArquivo(arquivo))
    },
    /** Descarta o estado de upload de UM item — chamado no remover da fila. */
    limparStatusUpload(arquivo) {
      const chave = chaveDoArquivo(arquivo)
      this.$delete(this.statusUploadPorChave, chave)
      this.$delete(this.uploadPorChave, chave)
      this.$delete(this.mensagemErroPorChave, chave)
    },
    /**
     * ★ [T009, spec-009] Fecha o envio: some da fila SÓ quem foi (status
     * `enviado`) — o resto (`enviando`/`erro`/`sem_cliente`) continua
     * esperando o próximo envio, com o status/mensagem intactos.
     *
     * Substituiu `limparTodosOsUploads()` (17/09/2026), que esvaziava a fila
     * INTEIRA — correto enquanto o invariante era "só envia com tudo
     * pronto" (então nada bloqueado sobrava para preservar). Hoje o envio
     * nunca espera todo anexo, e o que não foi precisa sobreviver ao envio
     * — exatamente o que `itensNaoIncluidosNoEnvio` relatou no turno.
     */
    removerAnexosEnviados() {
      const restantes = []
      this.arquivos.forEach((arquivo) => {
        const chave = chaveDoArquivo(arquivo)
        if (this.statusUploadPorChave[chave] === STATUS_UPLOAD.ENVIADO) {
          this.$delete(this.statusUploadPorChave, chave)
          this.$delete(this.uploadPorChave, chave)
          return
        }
        restantes.push(arquivo)
      })
      this.arquivos = restantes
    },
  },
}
